import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { RoleSummaryDto } from '../../web-api-client';
import { useTranslation } from 'react-i18next';

interface UserFormDialogProps {
  open: boolean;
  title: string;
  roles: RoleSummaryDto[];
  initialValues?: UserFormValues;
  isSubmitting?: boolean;
  isLoading?: boolean;
  errorMessage?: string | null;
  mode: 'create' | 'edit';
  onClose: () => void;
  onSubmit: (values: UserFormValues) => void;
}

export interface UserFormValues {
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  roleIds: string[];
}

interface UserFormErrors {
  email?: string;
  firstName?: string;
  lastName?: string;
  roleIds?: string;
}

const defaultValues: UserFormValues = {
  email: '',
  firstName: '',
  lastName: '',
  companyName: '',
  roleIds: [],
};

const UserFormDialog: React.FC<UserFormDialogProps> = ({
  open,
  title,
  roles,
  initialValues,
  isSubmitting = false,
  isLoading = false,
  errorMessage,
  mode,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<UserFormValues>(defaultValues);
  const [errors, setErrors] = useState<UserFormErrors>({});

  useEffect(() => {
    if (open) {
      setValues({
        email: initialValues?.email ?? '',
        firstName: initialValues?.firstName ?? '',
        lastName: initialValues?.lastName ?? '',
        companyName: initialValues?.companyName ?? '',
        roleIds: initialValues?.roleIds ?? [],
      });
      setErrors({});
    }
  }, [open, initialValues, mode]);

  const roleOptions = useMemo(() => roles, [roles]);

  const handleSubmit = () => {
    const validationErrors: UserFormErrors = {};

    if (!values.email.trim()) {
      validationErrors.email = t('users.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      validationErrors.email = t('users.validation.emailInvalid');
    }

    if (!values.firstName.trim()) {
      validationErrors.firstName = t('users.validation.firstNameRequired');
    }

    if (!values.lastName.trim()) {
      validationErrors.lastName = t('users.validation.lastNameRequired');
    }

    if (values.roleIds.length === 0) {
      validationErrors.roleIds = t('users.validation.rolesRequired');
    }

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      onSubmit({
        email: values.email.trim(),
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        companyName: values.companyName.trim(),
        roleIds: values.roleIds,
      });
    }
  };

  const selectedRoles = roleOptions.filter((role) => {
    const roleId = role.id ?? '';
    return values.roleIds.includes(roleId);
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, paddingTop: 2 }}>
        {isLoading ? (
          <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="textSM" color="text.secondary">
              {t('users.loadingUser')}
            </Typography>
          </Box>
        ) : (
          <>
            <TextField
              label={t('users.fields.email')}
              value={values.email}
              onChange={(event) => setValues((prev) => ({ ...prev, email: event.target.value }))}
              error={Boolean(errors.email)}
              helperText={errors.email}
              disabled={isSubmitting || mode === 'edit'}
              autoFocus
              type="email"
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label={t('users.fields.firstName')}
                value={values.firstName}
                onChange={(event) => setValues((prev) => ({ ...prev, firstName: event.target.value }))}
                error={Boolean(errors.firstName)}
                helperText={errors.firstName}
                disabled={isSubmitting}
                fullWidth
              />

              <TextField
                label={t('users.fields.lastName')}
                value={values.lastName}
                onChange={(event) => setValues((prev) => ({ ...prev, lastName: event.target.value }))}
                error={Boolean(errors.lastName)}
                helperText={errors.lastName}
                disabled={isSubmitting}
                fullWidth
              />
            </Stack>

            {mode === 'create' && (
              <Typography variant="textSM" color="text.secondary">
                {t('users.invitationNote')}
              </Typography>
            )}

            <TextField
              label="Company Name"
              value={values.companyName}
              onChange={(event) => setValues((prev) => ({ ...prev, companyName: event.target.value }))}
              disabled={isSubmitting}
            />

            <Box>
              <Typography variant="textSM" sx={{ fontWeight: 600, mb: 1 }}>
                {t('users.fields.roles')}
              </Typography>
              <Autocomplete
                multiple
                options={roleOptions}
                disableCloseOnSelect
                value={selectedRoles}
                onChange={(_, newValue) =>
                  setValues((prev) => ({
                    ...prev,
                    roleIds: newValue
                      .map((option) => option.id)
                      .filter((id): id is string => typeof id === 'string'),
                  }))
                }
                getOptionLabel={(option) => option.name ?? ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('users.fields.rolesPlaceholder')}
                    error={Boolean(errors.roleIds)}
                    helperText={errors.roleIds}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                      <Checkbox checked={selected} sx={{ ml: -1 }} />
                      <Box>
                        <Typography variant="textMD" sx={{ fontWeight: 600, display: 'block' }}>
                          {option.name}
                        </Typography>
                        {option.description && (
                          <Typography
                            variant="textXS"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.25 }}
                          >
                            {option.description}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </li>
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                      size="small"
                    />
                  ))
                }
                disabled={isSubmitting}
                fullWidth
              />
            </Box>

            {errorMessage && (
              <Typography color="error" variant="textSM">
                {errorMessage}
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting || isLoading}>
          {isSubmitting ? t('common.saving') : mode === 'create' ? t('users.actions.sendInvitation') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserFormDialog;
